import { useContext, useEffect, useState } from 'react';
import api from '../api';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Store } from '../Store';
import { getError } from '../utils';
import { PASSWORD_PATTERN } from '../utils/passwordRule';
import Popover from 'react-bootstrap/Popover';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { useTranslation } from 'react-i18next';

export default function ResetPasswordScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams(); // gets the token from the url

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);

  const { state } = useContext(Store);
  const { userInfo } = state;

  useEffect(() => {
    if (userInfo || !token) {
      navigate('/');
    }
  }, [navigate, userInfo, token]); // if the user already exists (is signed in) or the token doesn't exist, the user can't access this Screen

  const submitHandler = async (e) => {
    e.preventDefault(); // prevents a page refresh when the form will be submited
    if (password !== confirmPassword) {
      toast.error(t('reset-password-screen-error-1'));
      return;
    } // entered passwords must match
    try {
      await api.post('/api/users/reset-password', {
        password,
        token,
      }); // backend request for reseting the users password
      navigate('/signin');
      toast.success(t('reset-password-screen-success-1'));
    } catch (err) {
      toast.error(t(getError(err)));
    }
  };

  return (
    <Container className="small-container">
      <title>{t('reset-password-screen-helmet')}</title>

      <h1 className="screen-heading">{t('reset-password-screen-heading')}</h1>

      <Form className="reset-password-form" onSubmit={submitHandler}>
        <Form.Group controlId="password">
          <Form.Label className="display-inline">
            {t('reset-password-screen-new-password')}

            <OverlayTrigger
              trigger="click"
              rootClose // close the popover when the user clicks outside of it
              placement="bottom"
              overlay={
                <Popover id="popover-password">
                  <Popover.Header as="h3">
                    {t('reset-password-screen-popover-heading')}
                  </Popover.Header>
                  <Popover.Body>
                    <ul>
                      <li>{t('reset-password-screen-popover-li-1')}</li>
                      <li>{t('reset-password-screen-popover-li-2')}</li>
                      <li>{t('reset-password-screen-popover-li-3')}</li>
                      <li>{t('reset-password-screen-popover-li-4')}</li>
                      <li>{t('reset-password-screen-popover-li-5')}</li>
                    </ul>
                  </Popover.Body>
                </Popover>
              }
            >
              <Button className="info-popover">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="#707070"
                  className="bi bi-info-circle"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                </svg>
              </Button>
            </OverlayTrigger>
          </Form.Label>

          <Form.Control
            className="mobile-device-compatible"
            type={passwordVisible ? 'text' : 'password'}
            pattern={PASSWORD_PATTERN}
            required
            onChange={(e) => setPassword(e.target.value)}
          />
        </Form.Group>

        <Form.Group controlId="confirmPassword">
          <Form.Label>
            {t('reset-password-screen-confirm-new-password')}
          </Form.Label>

          <Form.Control
            className="mobile-device-compatible"
            type={passwordVisible ? 'text' : 'password'}
            pattern={PASSWORD_PATTERN}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="show-password" controlId="passwordVisibility">
          <Form.Check
            className="view-password"
            type="checkbox"
            label={t('reset-password-screen-show-password')}
            onChange={() => setPasswordVisible(!passwordVisible)}
            checked={passwordVisible}
          />
        </Form.Group>

        <div className="reset-password-submit">
          <Button variant="secondary" type="submit">
            {t('reset-password-screen-save')}
          </Button>
        </div>
      </Form>
    </Container>
  );
}
